import {
  ArrowLeft,
  GripVertical,
  Plus,
  Settings2,
  Trash2,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  useFeeds,
  usePlaylist,
  useSchedules,
  useSlotsForSchedules,
} from "../../api/hooks.js";
import { deletePlaylist, replacePlaylistItems } from "../../api/programming.js";
import { Button } from "../../design-system/button.js";
import { cn } from "../../design-system/cn.js";
import { InlineConfirm } from "../../design-system/inline-confirm.js";
import { Notice, Panel, StateSurface } from "../../design-system/surfaces.js";
import { PageHeading } from "../shared/page-heading.js";
import { AddTracksDialog } from "./add-tracks-dialog.js";
import { PlaylistDialog } from "./playlist-dialog.js";
import { moveBy, moveItem, orderChanged } from "./reorder.js";
import { feedsUsingPlaylist } from "./usage.js";
import { UsedBy } from "./used-by.js";
import type { PlaylistItem, Track } from "../../api/types.js";

/**
 * One playlist, and the order it plays in.
 *
 * The order is written whole: `PUT /api/playlists/{id}/items` replaces the list and
 * assigns the positions itself, so a reorder is one request that either takes or does
 * not. It is sent as soon as the operator stops moving things — a playlist editor that
 * makes you press Save has lost a change every time somebody navigates away — and if
 * the API refuses, the list goes back to what the API holds and says why.
 *
 * Dragging and the arrow keys are the same operation on the same list. The handle is a
 * real button with a name, so reordering by keyboard is not a lesser path to the same
 * end; each move is announced, because a list that reorders silently is unusable
 * without sight of it.
 */
const SAVE_DEBOUNCE_MS = 400;

export function PlaylistPage() {
  const { playlistId = "" } = useParams();
  const navigate = useNavigate();
  const playlist = usePlaylist(playlistId, true);
  const feeds = useFeeds(true);
  const schedules = useSchedules(true);

  const assignedScheduleIds = useMemo(
    () =>
      [
        ...new Set(
          (feeds.data ?? [])
            .map((feed) => feed.scheduleId)
            .filter((scheduleId) => scheduleId !== null),
        ),
      ].filter((scheduleId) =>
        (schedules.data ?? []).some((schedule) => schedule.id === scheduleId),
      ),
    [feeds.data, schedules.data],
  );
  const slots = useSlotsForSchedules(assignedScheduleIds);

  const [items, setItems] = useState<PlaylistItem[]>([]);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">(
    "idle",
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [editingSettings, setEditingSettings] = useState(false);
  const [adding, setAdding] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const handles = useRef(new Map<string, HTMLButtonElement | null>());
  const server = useRef<PlaylistItem[]>([]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const loaded = playlist.data?.items ?? [];
    server.current = loaded;
    setItems(loaded);
  }, [playlist.data]);

  const save = useCallback(
    (next: readonly PlaylistItem[]) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      setSaveState("saving");
      setSaveError(null);
      saveTimer.current = setTimeout(() => {
        replacePlaylistItems(
          playlistId,
          next.map((item) => ({ trackId: item.trackId, weight: item.weight })),
        )
          .then((saved) => {
            // The API assigns the positions, so what it returns is the order, and
            // it is what the screen holds from here on.
            const titled = saved.map((item, index) => ({
              ...item,
              title: next[index]?.title ?? item.title,
              artist: next[index]?.artist ?? item.artist,
            }));
            server.current = titled;
            setItems(titled);
            setSaveState("saved");
          })
          .catch((cause: unknown) => {
            setItems(server.current);
            setSaveState("idle");
            setSaveError(
              cause instanceof Error
                ? cause.message
                : "The order was not saved.",
            );
          });
      }, SAVE_DEBOUNCE_MS);
    },
    [playlistId],
  );

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  function commit(next: PlaylistItem[]) {
    setItems(next);
    if (orderChanged(server.current, next)) save(next);
  }

  function onHandleKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    const delta =
      event.key === "ArrowUp"
        ? -1
        : event.key === "ArrowDown"
          ? 1
          : event.key === "Home"
            ? -index
            : event.key === "End"
              ? items.length - 1 - index
              : 0;
    if (delta === 0) return;
    event.preventDefault();
    const moved = moveBy(items, index, delta);
    if (moved.index === index) return;
    commit(moved.items);
    const item = moved.items[moved.index];
    if (item) {
      setAnnouncement(
        `${item.title} moved to position ${String(moved.index + 1)} of ${String(moved.items.length)}`,
      );
      requestAnimationFrame(() => {
        handles.current.get(item.id)?.focus();
      });
    }
  }

  function onDrop(event: DragEvent<HTMLLIElement>, index: number) {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const moved = moveItem(items, dragIndex, index);
    setDragIndex(null);
    setOverIndex(null);
    commit(moved);
    const item = moved[index];
    if (item) {
      setAnnouncement(
        `${item.title} moved to position ${String(index + 1)} of ${String(moved.length)}`,
      );
    }
  }

  function addTracks(tracks: readonly Track[]) {
    const next = [
      ...items,
      ...tracks.map((track) => ({
        id: `pending-${track.id}`,
        trackId: track.id,
        position: 0,
        weight: 1,
        title: track.title,
        artist: track.artist,
      })),
    ];
    setItems(next);
    save(next);
  }

  function removeAt(index: number) {
    const removed = items[index];
    const next = items.filter((_, position) => position !== index);
    setItems(next);
    save(next);
    if (removed) setAnnouncement(`${removed.title} removed from the playlist`);
  }

  if (playlist.loading) {
    return <StateSurface kind="loading" title="Reading this playlist" />;
  }
  if (playlist.error || !playlist.data) {
    return (
      <StateSurface
        action={
          <Link
            className="text-sm font-semibold text-accent-text"
            to="/playlists"
          >
            Back to playlists
          </Link>
        }
        kind="error"
        title="This playlist could not be read"
      >
        {playlist.error ?? "No playlist has that address."}
      </StateSurface>
    );
  }

  const detail = playlist.data;
  const uses = feedsUsingPlaylist(detail.id, feeds.data ?? [], slots);

  return (
    <div className="flex flex-col gap-6">
      <Link
        className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-ink-3 transition-colors hover:text-ink"
        to="/playlists"
      >
        <ArrowLeft aria-hidden="true" className="size-3.5" />
        Playlists
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeading
          subtitle={`${String(items.length)} ${items.length === 1 ? "track" : "tracks"} · ${MODE_LABELS[detail.mode] ?? detail.mode}${detail.enabled ? "" : " · disabled"}`}
          title={detail.name}
        />
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setEditingSettings(true);
            }}
            variant="secondary"
          >
            <Settings2 aria-hidden="true" />
            Settings
          </Button>
          <Button
            onClick={() => {
              setAdding(true);
            }}
          >
            <Plus aria-hidden="true" />
            Add tracks
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="flex min-w-0 flex-col gap-3">
          {saveError ? <Notice tone="danger">{saveError}</Notice> : null}

          <div className="flex items-center justify-between gap-3">
            <p className="font-mono text-[0.6875rem] tracking-[0.04em] text-ink-3">
              {detail.mode === "ordered"
                ? "Plays top to bottom"
                : detail.mode === "weighted"
                  ? "Drawn at random, in proportion to weight"
                  : "Drawn at random"}
            </p>
            <p
              aria-live="polite"
              className="font-mono text-[0.6875rem] text-ink-3"
            >
              {saveState === "saving"
                ? "Saving order…"
                : saveState === "saved"
                  ? "Order saved"
                  : ""}
            </p>
          </div>

          {items.length === 0 ? (
            <StateSurface
              action={
                <Button
                  onClick={() => {
                    setAdding(true);
                  }}
                  size="small"
                >
                  <Plus aria-hidden="true" />
                  Add tracks
                </Button>
              }
              kind="empty"
              title="Nothing in this playlist yet"
            >
              A feed pointed at an empty playlist has nothing to play.
            </StateSurface>
          ) : (
            <ol className="overflow-hidden rounded-md border border-border bg-surface">
              {items.map((item, index) => (
                <li
                  key={item.id}
                  className={cn(
                    "flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0",
                    dragIndex === index && "opacity-40",
                    overIndex === index &&
                      dragIndex !== null &&
                      dragIndex !== index &&
                      "bg-accent-soft",
                  )}
                  draggable
                  onDragEnd={() => {
                    setDragIndex(null);
                    setOverIndex(null);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setOverIndex(index);
                  }}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = "move";
                    // Firefox will not start a drag without payload.
                    event.dataTransfer.setData("text/plain", item.id);
                    setDragIndex(index);
                  }}
                  onDrop={(event) => {
                    onDrop(event, index);
                  }}
                >
                  <button
                    aria-label={`Reorder ${item.title}, position ${String(index + 1)} of ${String(items.length)}`}
                    className="grid size-8 shrink-0 cursor-grab place-items-center rounded-sm text-ink-3 transition-colors hover:bg-surface-hi hover:text-ink"
                    onKeyDown={(event) => {
                      onHandleKeyDown(event, index);
                    }}
                    ref={(element) => {
                      handles.current.set(item.id, element);
                    }}
                    title="Drag, or move with the arrow keys"
                    type="button"
                  >
                    <GripVertical aria-hidden="true" className="size-4" />
                  </button>
                  <span className="w-6 shrink-0 text-right font-mono text-[0.6875rem] text-ink-3 tabular-nums">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">
                      {item.title}
                    </span>
                    <span className="block truncate text-xs text-ink-3">
                      {item.artist || "Unknown artist"}
                    </span>
                  </span>
                  {detail.mode === "weighted" ? (
                    <span className="shrink-0 font-mono text-[0.6875rem] text-ink-3">
                      ×{item.weight}
                    </span>
                  ) : null}
                  <button
                    aria-label={`Remove ${item.title} from this playlist`}
                    className="grid size-8 shrink-0 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-surface-hi hover:text-err"
                    onClick={() => {
                      removeAt(index);
                    }}
                    type="button"
                  >
                    <X aria-hidden="true" className="size-4" />
                  </button>
                </li>
              ))}
            </ol>
          )}

          <p aria-live="polite" className="sr-only">
            {announcement}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <Panel className="p-4">
            <h2 className="text-sm font-semibold text-ink">Used by</h2>
            <p className="mt-1 text-xs leading-5 text-ink-3">
              Feeds using this playlist. Changes apply to all of them.
            </p>
            <UsedBy className="mt-3" showRoles uses={uses} />
          </Panel>

          <Panel className="p-4">
            <h2 className="text-sm font-semibold text-ink">Rules</h2>
            <dl className="mt-3 grid gap-1.5 font-mono text-[0.6875rem] text-ink-3">
              <div className="flex justify-between gap-3">
                <dt>Mode</dt>
                <dd className="text-ink-2">
                  {MODE_LABELS[detail.mode] ?? detail.mode}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt>Track separation</dt>
                <dd className="text-ink-2">
                  {String(detail.minTrackSeparationMinutes)} min
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt>Artist separation</dt>
                <dd className="text-ink-2">
                  {String(detail.minArtistSeparationMinutes)} min
                </dd>
              </div>
            </dl>
          </Panel>

          <InlineConfirm
            confirmLabel="Delete playlist"
            onConfirm={() => {
              deletePlaylist(detail.id)
                .then(() => navigate("/playlists"))
                .catch((cause: unknown) => {
                  setSaveError(
                    cause instanceof Error
                      ? cause.message
                      : "The playlist was not deleted.",
                  );
                });
            }}
            question="Delete this playlist?"
            trigger={(open) => (
              <Button onClick={open} size="small" variant="ghost">
                <Trash2 aria-hidden="true" />
                Delete playlist
              </Button>
            )}
          >
            {uses.length > 0
              ? `${String(uses.length)} ${uses.length === 1 ? "feed draws" : "feeds draw"} on it.`
              : "No feed draws on it."}
          </InlineConfirm>
        </div>
      </div>

      <PlaylistDialog
        onClose={() => {
          setEditingSettings(false);
        }}
        onSaved={playlist.reload}
        open={editingSettings}
        playlist={detail}
      />

      <AddTracksDialog
        alreadyIn={new Set(items.map((item) => item.trackId))}
        onAdd={addTracks}
        onClose={() => {
          setAdding(false);
        }}
        open={adding}
      />
    </div>
  );
}

const MODE_LABELS: Record<string, string> = {
  ordered: "Ordered",
  shuffle: "Shuffle",
  weighted: "Weighted",
};
